import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LockKeyhole, Mail } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "wouter";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { setAccessToken } from "@/store/authStore";
import { navigate } from "wouter/use-browser-location";
import { useQueryClient } from "@tanstack/react-query";
// import logo from "/asset/MySoft_Quickbill_logo_500x300.png";
import logo from "/asset/logo-removebg-preview.png";
import { AxiosError } from "axios";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [_, setLocation] = useLocation();
  const [dialogMessage, setDialogMessage] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const { login, isLoading } = useAuth();
  const queryClient = useQueryClient();
  const version = import.meta.env.VITE_APP_VERSION;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setDialogMessage("Please enter both email and password.");
      setShowDialog(true);
      return;
    }

    login(
      { email, password },
      {
        onSuccess: (data) => {
          if (!data.accessToken) {
            return;
          }

          setAccessToken(data.accessToken);
          queryClient.invalidateQueries({ queryKey: ["user"] });
          navigate("/");
        },
        onError: (error) => {
          if (error instanceof AxiosError) {
            if (error.response?.status === 401) {
              setDialogMessage("Invalid email or password.");
            }
            if (error.response?.status === 403) {
              if(error.response.data.message === "Email is not verified."){
                navigate(`/email-not-verified?email=${email}`);
              }
              setDialogMessage(error.response.data.message)
            }
          } else {
            setDialogMessage("Please try again.");
          }
          setShowDialog(true);
        },
      }
    );
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-blue-600 to-purple-700">
      {/* Animated Background */}
      <div className="absolute inset-0" style={{ opacity: 0.8 }}>
        <svg
          className="w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          role="img"
          aria-labelledby="waveTitle"
        >
          <title id="waveTitle">Animated wave background</title>
          {/* Wave Group 1 */}
          <path
            fill="rgba(59, 130, 246, 0.2)"
            d="M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z"
            className="animate-wave-slow"
          >
            <animate
              attributeName="d"
              dur="10s"
              repeatCount="indefinite"
              values="
                M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z;
                M-150 30 C-100 40, -50 50, 0 30 S 50 40, 100 50 S 150 40, 200 30 V100 H-150 Z;
                M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z"
            />
          </path>

          {/* Wave Group 2 */}
          <path
            fill="rgba(139, 92, 246, 0.2)"
            d="M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z"
            className="animate-wave-medium"
          >
            <animate
              attributeName="d"
              dur="8s"
              repeatCount="indefinite"
              values="
                M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z;
                M-150 40 C-100 50, -50 60, 0 40 S 50 50, 100 60 S 150 50, 200 40 V100 H-150 Z;
                M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z"
            />
          </path>

          {/* Wave Group 3 */}
          <path
            fill="rgba(99, 102, 241, 0.2)"
            d="M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z"
            className="animate-wave-fast"
          >
            <animate
              attributeName="d"
              dur="6s"
              repeatCount="indefinite"
              values="
                M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z;
                M-150 50 C-100 60, -50 70, 0 50 S 50 60, 100 70 S 150 60, 200 50 V100 H-150 Z;
                M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z"
            />
          </path>
        </svg>

        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={`particle-${i}-${Math.random().toString(36)}`}
              className="absolute rounded-full bg-white/10 animate-float"
              style={{
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${Math.random() * 10 + 10}s`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Login Card */}
      <Card className="w-full max-w-md mx-4 bg-white/90 backdrop-blur-sm shadow-xl relative z-10">
        <div className="flex flex-col">
          <img className="mt-3 mx-auto w-48" src={logo} />
        </div>
        <CardHeader className="space-y-1 pt-0">
          <CardTitle className="text-lg text-center font-medium">
            Cloud E Invoice
          </CardTitle>
          <p className="text-center text-gray-600">
            Please sign in to continue
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="relative">
                <LockKeyhole className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 "
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Log in"}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <a
              href="/forgot-password"
              className="text-blue-600 hover:underline"
            >
              Forgot password?
            </a>
            <span className="mx-2">•</span>
            <button
              className="text-blue-600 hover:underline"
              onClick={() => setLocation("/register")}
            >
              Sign up
            </button>
          </div>
        </CardContent>
      </Card>
      <p className="text-xs text-white font-medium absolute left-3 bottom-2">
        {version}
      </p>
      <style>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0) translateX(0);
          }
          25% {
            transform: translateY(-20px) translateX(10px);
          }
          50% {
            transform: translateY(-35px) translateX(-10px);
          }
          75% {
            transform: translateY(-20px) translateX(8px);
          }
        }

        .animate-float {
          animation: float linear infinite;
        }

        .animate-wave-slow {
          animation: wave 15s ease-in-out infinite;
        }

        .animate-wave-medium {
          animation: wave 12s ease-in-out infinite;
        }

        .animate-wave-fast {
          animation: wave 9s ease-in-out infinite;
        }

        @keyframes wave {
          0%,
          100% {
            transform: translateX(-50%);
          }
          50% {
            transform: translateX(0%);
          }
        }
      `}</style>

      {/* Alert Dialog for messages */}
      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogTitle>Login Failed</AlertDialogTitle>
          <AlertDialogDescription>{dialogMessage}</AlertDialogDescription>
          <AlertDialogFooter>
            <Button
              onClick={() => {
                setShowDialog(false);
              }}
            >
              OK
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LoginPage;
